import {
    Component,
    ElementRef,
    forwardRef,
    HostBinding,
    input,
    model,
    OnChanges,
    output,
    SimpleChanges,
    viewChild
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    UntypedFormControl,
    Validator
} from '@angular/forms';
import {
    createSeparatorMask,
    handleCaret,
    isSafari,
    unMaskSeparatorValue
} from '@trade-platform/ui-utils';
import { AixDataTestingDirective } from '../../directives/data-testing/data-testing.directive';

@Component({
    selector: 'aix-ssn',
    styleUrls: ['./aix-ssn.component.scss'],
    templateUrl: './aix-ssn.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => AixSsnComponent)
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AixSsnComponent
        }
    ],
    standalone: true,
    imports: [AixDataTestingDirective]
})
export class AixSsnComponent implements ControlValueAccessor, Validator, OnChanges {
    _propagateChanges: (value: string) => void = () => ({});
    _propagateTouches: () => void = () => ({});

    config = input<any>();
    isStandalone = input<boolean>(true);
    isRequired = input<boolean>(false);
    isDisabled = input<boolean>(false);
    isValid = input<boolean>(false);
    isDirty = model<boolean>(false);
    initialValue = input<string>();
    hint = input<string>('');

    valueChanges = output<string>();
    setDirty = output();
    onFocus = output<Event>();
    onBlur = output<Event>();

    inputField = viewChild<ElementRef<HTMLInputElement>>('inputField');

    @HostBinding('attr.aix-control')
    aixControl: string;

    private readonly MAX_LENGTH = 9;
    private caretPositionStart: number;
    private caretPositionEnd: number;
    private keyDownEvent: KeyboardEvent;
    private mask = createSeparatorMask([3, 5], this.MAX_LENGTH, '-');

    constructor() {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.initialValue) {
            this.valueToSend(this.initialValue() || '');
        }
    }

    onKeyDown(e: KeyboardEvent & { target: any }) {
        this.caretPositionStart = e.target.selectionStart;
        this.caretPositionEnd = e.target.selectionEnd;
        this.keyDownEvent = e;
    }

    valueToSend(rawValue: string) {
        const unmaskedValue = unMaskSeparatorValue(rawValue, this.MAX_LENGTH);
        (<ElementRef>this.inputField()).nativeElement.value = this.mask(unmaskedValue as string);

        if (isSafari) {
            handleCaret(
                [3, 6],
                this.keyDownEvent,
                (<ElementRef>this.inputField()).nativeElement as HTMLInputElement,
                this.caretPositionStart,
                this.caretPositionEnd
            );
        }

        return unmaskedValue && unmaskedValue.length ? unmaskedValue : '';
    }

    onUserInput(evt: Event | ClipboardEvent) {
        const val = this.valueToSend((evt.target as HTMLInputElement).value);
        if (!this.isDirty()) {
            this.setDirty.emit();

            // It's standalone, we set the dirty state here instead through the store
            if (this.isStandalone()) {
                this.isDirty.set(true);
            }
        }
        this._propagateChanges(val);
        this.valueChanges.emit(val);
    }

    onFocusInput(evt: Event | ClipboardEvent) {
        this.onFocus.emit(evt);
    }

    onBlurInput(evt: Event | ClipboardEvent) {
        if (this.isDirty()) {
            this.onBlur.emit(evt);
        }
    }

    validate({ value }: UntypedFormControl) {
        return !value || value.match(/\d\d\d\d\d\d\d\d\d/g) ? null : { invalid: true };
    }

    writeValue(value: any) {
        this.valueToSend(value);
    }

    registerOnChange(fn: (value: string) => void) {
        this._propagateChanges = fn;
    }

    registerOnTouched(fn: () => void) {
        this._propagateTouches = fn;
    }
}
